import { createFileRoute, Link, useNavigate, notFound } from "@tanstack/react-router";
import { PracticeSafetyNote } from "@/components/safety/SafetyNotices";
import { claimAudio, registerAudioOwner, type AudioOwner } from "@/lib/audio-bus";
import { withMemberGate } from "@/lib/with-member-gate";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Music,
  Music2,
  Captions,
  Type,
  X,
  PlayCircle,
} from "lucide-react";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { CATEGORIES, ExerciseGrid, type Exercise } from "@/lib/toolkit-shared";

/* ─────────────── Route ─────────────── */

type CoreSlug =
  | "box-breathing"
  | "4-7-8"
  | "panic-reset"
  | "extended-exhale"
  | "belly-breathing";

type CoreConfig = {
  slug: CoreSlug;
  linkSlug: string; // URL path segment (may differ from exercise slug for 4-7-8)
  emoji: string;
  title: string;
  subtitle: string;
  durationLabel: string;
  gradient: string;
  posterEmoji: string;
  transcript: { emoji: string; title: string; blurb: string }[];
  videoBase: string; // /videos/core-<name>
};

const CORE_CONFIGS: Record<string, CoreConfig> = {
  "box-breathing": {
    slug: "box-breathing",
    linkSlug: "box-breathing",
    emoji: "🌬️",
    title: "Box Breathing",
    subtitle:
      "Equal-count breathing for steady rhythm, grounding, focus and nervous-system regulation. Breath holds are optional.",
    durationLabel: "Approximately 3–5 minutes",
    gradient: "from-[#dfe7ec] via-[#c9d6df] to-[#b3c3d1]",
    posterEmoji: "🌬️",
    videoBase: "/videos/core-box-breathing",
    transcript: [
      {
        emoji: "🌿",
        title: "Welcome",
        blurb:
          "A steady, equal-count breath to help you feel grounded and focused. Breath holds are optional — go at your own pace.",
      },
      {
        emoji: "🫁",
        title: "Inhale (4 counts)",
        blurb:
          "A slow, comfortable breath in through the nose, drawing the count of four.",
      },
      {
        emoji: "⏸",
        title: "Hold (optional)",
        blurb:
          "A gentle pause. Skip the hold if it doesn't feel right — a smooth transition is fine.",
      },
      {
        emoji: "🌿",
        title: "Exhale (4 counts)",
        blurb:
          "A soft breath out for the count of four, letting the shoulders drop.",
      },
      {
        emoji: "⏸",
        title: "Rest (optional)",
        blurb: "A brief pause before the next round — or move straight in.",
      },
      {
        emoji: "✨",
        title: "Closing",
        blurb:
          "Notice how you feel. Even one or two rounds is a meaningful pause.",
      },
    ],
  },
  "4-7-8": {
    slug: "4-7-8",
    linkSlug: "4-7-8-breathing",
    emoji: "🌙",
    title: "4-7-8 Breathing",
    subtitle:
      "A calming pattern with a longer exhale — for relaxation, sleep preparation, and quieting racing thoughts. Never force the counts.",
    durationLabel: "Approximately 3–5 minutes",
    gradient: "from-[#e6dcef] via-[#d4c6e2] to-[#b9a8cf]",
    posterEmoji: "🌙",
    videoBase: "/videos/core-4-7-8",
    transcript: [
      {
        emoji: "🌿",
        title: "Welcome",
        blurb:
          "A gentle 4-7-8 rhythm to slow the breath. If the counts feel long, shorten them — you're welcome to breathe at your own pace.",
      },
      {
        emoji: "🫁",
        title: "Inhale for 4",
        blurb: "A quiet breath in through the nose for a count of four.",
      },
      {
        emoji: "⏸",
        title: "Hold for 7 (if comfortable)",
        blurb:
          "A soft hold. If it feels too long, hold for a count you can manage.",
      },
      {
        emoji: "🌬️",
        title: "Exhale for 8",
        blurb:
          "A slow, complete breath out through the mouth. The long exhale is the calming part.",
      },
      {
        emoji: "🤍",
        title: "Rest",
        blurb: "A quiet moment before the next round.",
      },
      {
        emoji: "✨",
        title: "Closing",
        blurb: "Return to your natural breath. Notice the softening.",
      },
    ],
  },
  "panic-reset": {
    slug: "panic-reset",
    linkSlug: "panic-reset",
    emoji: "🆘",
    title: "Panic Reset",
    subtitle:
      "A quick breathing reset for moments of panic or intense anxiety. Calm, reassuring, immediate grounding.",
    durationLabel: "Approximately 2–4 minutes",
    gradient: "from-[#f0e2dc] via-[#e5cabd] to-[#d5a99a]",
    posterEmoji: "🆘",
    videoBase: "/videos/core-panic-reset",
    transcript: [
      {
        emoji: "🤍",
        title: "You're safe here",
        blurb:
          "This will pass. You don't need to breathe perfectly — just follow along as best you can.",
      },
      {
        emoji: "👃",
        title: "Double inhale",
        blurb:
          "A small breath in through the nose, then a second little sip on top of it to fill the lungs.",
      },
      {
        emoji: "🌬️",
        title: "Long, slow exhale",
        blurb:
          "A long, soft breath out through the mouth. The long exhale is what tells your body it's safe.",
      },
      {
        emoji: "🖐️",
        title: "Ground into the body",
        blurb:
          "Feel your feet on the floor. Notice one thing you can see and one thing you can touch.",
      },
      {
        emoji: "✨",
        title: "Closing",
        blurb:
          "The wave is passing. You did the right thing by pausing — you can return here anytime.",
      },
    ],
  },
  "extended-exhale": {
    slug: "extended-exhale",
    linkSlug: "extended-exhale",
    emoji: "🤍",
    title: "Extended Exhale Breathing",
    subtitle:
      "A longer exhale to return to calm, soften tension, and support emotional regulation. Slow and natural — no forcing.",
    durationLabel: "Approximately 3–5 minutes",
    gradient: "from-[#e7ecd9] via-[#d3dcc0] to-[#b7c69b]",
    posterEmoji: "🤍",
    videoBase: "/videos/core-extended-exhale",
    transcript: [
      {
        emoji: "🌿",
        title: "Welcome",
        blurb:
          "The exhale is the part of the breath that helps the body settle. We'll simply make it a little longer than the inhale.",
      },
      {
        emoji: "🫁",
        title: "Gentle inhale",
        blurb: "A soft breath in through the nose — no effort needed.",
      },
      {
        emoji: "🌬️",
        title: "Longer exhale",
        blurb:
          "A slow, unhurried breath out — a little longer than the inhale.",
      },
      {
        emoji: "🤍",
        title: "Soften",
        blurb: "Let the jaw, shoulders and belly soften a fraction more.",
      },
      {
        emoji: "✨",
        title: "Closing",
        blurb:
          "Return to your natural rhythm. Notice any small shift — that's enough.",
      },
    ],
  },
  "belly-breathing": {
    slug: "belly-breathing",
    linkSlug: "belly-breathing",
    emoji: "🌱",
    title: "Belly Breathing",
    subtitle:
      "Diaphragmatic breathing with gentle body awareness — the foundation of a daily breathing practice.",
    durationLabel: "Approximately 4–6 minutes",
    gradient: "from-[#f0e6d0] via-[#e4d1a8] to-[#c7b07f]",
    posterEmoji: "🌱",
    videoBase: "/videos/core-belly-breathing",
    transcript: [
      {
        emoji: "🌿",
        title: "Welcome",
        blurb:
          "If it feels comfortable, place one hand on your chest and one on your belly.",
      },
      {
        emoji: "🫁",
        title: "Breathe into the belly",
        blurb:
          "Breathe in gently through the nose so the belly hand rises more than the chest hand.",
      },
      {
        emoji: "🌬️",
        title: "Slow release",
        blurb:
          "Exhale slowly, letting the belly soften back toward the spine.",
      },
      {
        emoji: "🤍",
        title: "Body awareness",
        blurb:
          "Notice the warmth of your hands, the rise and fall, the pause between breaths.",
      },
      {
        emoji: "🌱",
        title: "Building a practice",
        blurb:
          "Even a few belly breaths a day is enough to begin building the practice.",
      },
      {
        emoji: "✨",
        title: "Closing",
        blurb:
          "Return to your natural breath. Notice how the body feels now.",
      },
    ],
  },
};

// Route path uses the linkSlug (URL segment)
type RouteParams = { slug: string };

export const Route = createFileRoute("/breathing/core/$slug")({
  beforeLoad: ({ params }: { params: RouteParams }) => {
    const cfg = Object.values(CORE_CONFIGS).find(
      (c) => c.linkSlug === params.slug,
    );
    if (!cfg) throw notFound();
    return { cfg };
  },
  head: ({ params }) => {
    const cfg = Object.values(CORE_CONFIGS).find(
      (c) => c.linkSlug === (params as RouteParams).slug,
    );
    const title = cfg
      ? `${cfg.title} | Guided Breathing — Recovery Trauma™`
      : "Guided Breathing — Recovery Trauma™";
    const desc = cfg
      ? cfg.subtitle
      : "Guided breathing videos for the Recovery Trauma™ core library.";
    return {
      meta: [
        { name: "robots", content: "noindex" },
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: withMemberGate(CoreVideoPage, "Guided Breathing"),
});

/* ─────────────── Page ─────────────── */

function CoreVideoPage() {
  const params = Route.useParams();
  const navigate = useNavigate({ from: "/breathing/core/$slug" });
  const [textOnly, setTextOnly] = useState(false);

  const cfg = useMemo(
    () =>
      Object.values(CORE_CONFIGS).find((c) => c.linkSlug === params.slug) ??
      null,
    [params.slug],
  );

  const exercise = useMemo<Exercise | null>(() => {
    if (!cfg) return null;
    const breathing = CATEGORIES.find((c) => c.id === "breathing");
    return breathing?.exercises.find((e) => e.slug === cfg.slug) ?? null;
  }, [cfg]);

  const otherCore = useMemo(
    () =>
      Object.values(CORE_CONFIGS).filter((c) => c.linkSlug !== params.slug),
    [params.slug],
  );

  if (!cfg) return null;

  const openExercise = () => {
    if (!exercise) return;
    navigate({ to: "/breathing", search: { open: exercise.slug } });
  };

  return (
    <div className="mx-auto max-w-5xl px-4 pt-10 pb-8 md:pb-12">
      <Link
        to="/breathing"
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to Breathing
      </Link>

      <div className="mb-8 flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
          <span className="text-xl" aria-hidden>
            {cfg.emoji}
          </span>
        </div>
        <div>
          <h1 className="font-serif text-3xl text-foreground md:text-4xl">
            {cfg.emoji} {cfg.title}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{cfg.subtitle}</p>
        </div>
      </div>

      <PracticeSafetyNote kind="breathing" className="mb-4" />

      {!textOnly ? (
        <GuidedCoreVideo cfg={cfg} onTextOnly={() => setTextOnly(true)} />
      ) : (
        <TextOnlyMode
          transcript={cfg.transcript}
          onExit={() => setTextOnly(false)}
        />
      )}

      {/* Transcript */}
      <details className="mt-4 rounded-2xl border border-border/50 bg-cream/40 p-4 text-sm">
        <summary className="cursor-pointer font-semibold text-foreground">
          Transcript &amp; chapters ({cfg.durationLabel.toLowerCase()})
        </summary>
        <ul className="mt-3 space-y-2 text-muted-foreground">
          {cfg.transcript.map((c) => (
            <li key={c.title} className="flex gap-2">
              <span aria-hidden>{c.emoji}</span>
              <span>
                <span className="font-medium text-foreground">{c.title}.</span>{" "}
                {c.blurb}
              </span>
            </li>
          ))}
        </ul>
      </details>

      {/* This exercise card */}
      {exercise && (
        <section className="mt-10">
          <div className="mb-4">
            <h2 className="font-display text-2xl text-foreground">
              🌬 Begin the exercise
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Open the exercise and play the guided audio — go at your own pace.
            </p>
          </div>
          <ExerciseGrid
            exercises={[exercise]}
            onPick={openExercise}
            isBreathing
          />
        </section>
      )}

      {/* Other core videos */}
      <section className="mt-12">
        <h2 className="font-display text-2xl text-foreground">
          Other core practices
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Each foundation technique has its own dedicated guided video.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {otherCore.map((o) => (
            <Link
              key={o.linkSlug}
              to="/breathing/core/$slug"
              params={{ slug: o.linkSlug }}
              className="group flex items-center gap-3 rounded-2xl border border-border/50 bg-card p-4 transition hover:border-[var(--gold-deep)]/50 hover:bg-[var(--gold-soft)]/30"
            >
              <PlayCircle className="h-5 w-5 shrink-0 text-[var(--gold-deep)]" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground">
                  {o.emoji} {o.title}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {o.durationLabel}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <div className="mt-12 rounded-2xl border border-border/40 bg-cream/40 p-5 text-center text-sm text-muted-foreground">
        These practices support — they don't replace — therapy or medical care.{" "}
        <Link to="/resources" className="text-primary hover:underline">
          Crisis resources
        </Link>
        .
      </div>
      <div className="mt-8">
        <ComplianceNotice variant="practice" />
      </div>
    </div>
  );
}

/* ─────────────── Guided video player ─────────────── */

function GuidedCoreVideo({
  cfg,
  onTextOnly,
}: {
  cfg: CoreConfig;
  onTextOnly: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const ambientRef = useRef<HTMLAudioElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [ambientOn, setAmbientOn] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [videoAvailable, setVideoAvailable] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Reset when slug changes
  useEffect(() => {
    setIsPlaying(false);
    setVideoAvailable(true);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    if (ambientRef.current) {
      ambientRef.current.pause();
      ambientRef.current.currentTime = 0;
    }
  }, [cfg.videoBase]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const tracks = v.textTracks;
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].mode = captionsOn ? "showing" : "hidden";
    }
  }, [captionsOn]);

  // Video + ambient bed belong together, so the bus treats them as one player.
  const ownerRef = useRef<AudioOwner>({
    stop: () => {
      videoRef.current?.pause();
      ambientRef.current?.pause();
    },
    ownsElement: (el) => el === videoRef.current || el === ambientRef.current,
  });
  useEffect(() => registerAudioOwner(ownerRef.current), []);

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v || !videoAvailable) return;
    if (v.paused) {
      claimAudio(ownerRef.current);
      try {
        await v.play();
        if (ambientOn && ambientRef.current) {
          ambientRef.current.play().catch(() => {});
        }
      } catch {
        /* ignore */
      }
    } else {
      v.pause();
      ambientRef.current?.pause();
    }
  };

  const exitPlayback = () => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = 0;
    ambientRef.current?.pause();
    if (ambientRef.current) ambientRef.current.currentTime = 0;
  };

  const toggleVoice = () => {
    setVoiceOn((prev) => {
      const next = !prev;
      if (videoRef.current) videoRef.current.muted = !next;
      return next;
    });
  };

  const toggleAmbient = () => {
    setAmbientOn((prev) => {
      const next = !prev;
      const a = ambientRef.current;
      if (a) {
        a.loop = true;
        a.volume = 0.3;
        if (next && !videoRef.current?.paused) a.play().catch(() => {});
        else a.pause();
      }
      return next;
    });
  };

  return (
    <div className="overflow-hidden rounded-3xl border border-border/50 bg-black shadow-[var(--shadow-gold)]">
      <div
        className={`relative aspect-video w-full bg-gradient-to-br ${cfg.gradient}`}
      >
        {videoAvailable ? (
          <video
            key={cfg.videoBase}
            ref={videoRef}
            className="h-full w-full object-contain"
            playsInline
            preload="metadata"
            poster={`${cfg.videoBase}-poster.jpg`}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            onError={() => setVideoAvailable(false)}
            controlsList="nodownload"
          >
            <source src={`${cfg.videoBase}.mp4`} type="video/mp4" />
            <track
              kind="captions"
              src={`${cfg.videoBase}.en.vtt`}
              srcLang="en"
              label="English"
              default
            />
          </video>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center">
            <span className="text-4xl" aria-hidden>
              {cfg.posterEmoji}
            </span>
            <p className="max-w-md font-display text-lg text-foreground">
              {cfg.title} guided video coming soon
            </p>
            <p className="max-w-md text-sm text-muted-foreground">
              A professionally narrated {cfg.durationLabel.toLowerCase()}{" "}
              session will play here. In the meantime, begin the exercise
              below, or switch to text-only mode.
            </p>
          </div>
        )}

        {!reducedMotion && videoAvailable && !isPlaying && (
          <span
            aria-hidden
            className="pointer-events-none absolute end-6 top-6 text-2xl opacity-70"
            style={{ animation: "pulse 3s ease-in-out infinite" }}
          >
            {cfg.posterEmoji}
          </span>
        )}

        {videoAvailable && (
          <button
            type="button"
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause guided video" : "Play guided video"}
            className="absolute inset-0 m-auto flex h-20 w-20 items-center justify-center rounded-full bg-white/85 text-foreground shadow-lg backdrop-blur-sm transition hover:scale-105"
          >
            {isPlaying ? (
              <Pause className="h-8 w-8" />
            ) : (
              <Play className="ms-1 h-8 w-8" />
            )}
          </button>
        )}
      </div>

      <audio
        key={`${cfg.videoBase}-ambient`}
        ref={ambientRef}
        src={`${cfg.videoBase}-ambient.mp3`}
        preload="none"
      />

      <div className="flex flex-wrap items-center justify-between gap-3 bg-card px-4 py-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <ControlChip
            active={isPlaying}
            onClick={togglePlay}
            icon={
              isPlaying ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )
            }
            label={isPlaying ? "Pause" : "Play"}
            disabled={!videoAvailable}
          />
          <ControlChip
            active={voiceOn}
            onClick={toggleVoice}
            icon={
              voiceOn ? (
                <Volume2 className="h-4 w-4" />
              ) : (
                <VolumeX className="h-4 w-4" />
              )
            }
            label={voiceOn ? "Voice on" : "Voice off"}
            disabled={!videoAvailable}
          />
          <ControlChip
            active={ambientOn}
            onClick={toggleAmbient}
            icon={
              ambientOn ? (
                <Music className="h-4 w-4" />
              ) : (
                <Music2 className="h-4 w-4" />
              )
            }
            label={ambientOn ? "Ambient on" : "Ambient off"}
          />
          <ControlChip
            active={captionsOn}
            onClick={() => setCaptionsOn((v) => !v)}
            icon={<Captions className="h-4 w-4" />}
            label={captionsOn ? "Captions on" : "Captions off"}
          />
          <ControlChip
            active={false}
            onClick={onTextOnly}
            icon={<Type className="h-4 w-4" />}
            label="Text-only mode"
          />
        </div>
        <button
          type="button"
          onClick={exitPlayback}
          className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          disabled={!videoAvailable}
        >
          <X className="h-3.5 w-3.5" /> Exit
        </button>
      </div>
      <p className="border-t border-border/40 bg-cream/40 px-4 py-2 text-center text-xs text-muted-foreground">
        {cfg.durationLabel} • Pause or exit anytime • Reduced-motion friendly •
        Breathe at your own pace
      </p>
    </div>
  );
}

/* ─────────────── Text-only mode ─────────────── */

function TextOnlyMode({
  transcript,
  onExit,
}: {
  transcript: CoreConfig["transcript"];
  onExit: () => void;
}) {
  return (
    <div className="rounded-3xl border border-border/50 bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-xl text-foreground">
          Text-only guided journey
        </h2>
        <button
          type="button"
          onClick={onExit}
          className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border/60 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" /> Back to video
        </button>
      </div>
      <ol className="space-y-4 text-sm text-muted-foreground">
        {transcript.map((c, i) => (
          <li
            key={c.title}
            className="rounded-2xl border border-border/40 bg-cream/40 p-4"
          >
            <p className="font-medium text-foreground">
              {i + 1}. <span aria-hidden>{c.emoji}</span> {c.title}
            </p>
            <p className="mt-1">{c.blurb}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ─────────────── Small control chip ─────────────── */

function ControlChip({
  active,
  onClick,
  icon,
  label,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
        active
          ? "border-[var(--gold-deep)]/50 bg-[var(--gold-soft)]/60 text-foreground"
          : "border-border/60 bg-card text-muted-foreground hover:text-foreground"
      } disabled:cursor-not-allowed disabled:opacity-50`}
    >
      {icon}
      {label}
    </button>
  );
}

// Public list of core videos — for callouts on /breathing.
export const CORE_VIDEO_LINKS = Object.values(CORE_CONFIGS).map((c) => ({
  linkSlug: c.linkSlug,
  emoji: c.emoji,
  title: c.title,
  durationLabel: c.durationLabel,
}));
